<template>
  <div class="user-report">
    <!-- 头部：标题 + 时间范围 -->
    <div class="ur-header">
      <div class="ur-header__title">
        <h2>用户数据报表</h2>
        <p>用户行为与生命周期分析</p>
      </div>
      <div class="ur-header__actions">
        <button
          v-for="item in rangeOptions"
          :key="item.key"
          class="range-btn"
          :class="{ 'range-btn--active': range === item.key }"
          @click="selectRange(item.key)"
        >{{ item.label }}</button>
        <el-date-picker
          v-model="customFrom"
          type="date"
          size="mini"
          value-format="yyyy-MM-dd"
          placeholder="开始日期"
          :picker-options="pickerOptions"
          class="range-date"
          @change="onCustomRange"
        />
        <span class="range-sep">至</span>
        <el-date-picker
          v-model="customTo"
          type="date"
          size="mini"
          value-format="yyyy-MM-dd"
          placeholder="结束日期"
          :picker-options="pickerOptions"
          class="range-date"
          @change="onCustomRange"
        />
        <button class="range-btn range-btn--refresh" @click="refresh">🔄 刷新</button>
      </div>
    </div>

    <!-- 概览区块：KPI + 图表 + 时效 -->
    <div v-loading="overviewLoading" class="ur-overview">
      <!-- KPI 卡片 -->
      <div class="ur-kpi">
        <div v-for="item in kpi" :key="item.code" class="kpi-card">
          <div class="kpi-card__label">{{ item.label }}</div>
          <div class="kpi-card__value">{{ formatInt(item.value) }}</div>
          <div class="kpi-card__foot">
            <span v-if="item.changePct !== null && item.changePct !== undefined" class="kpi-change" :class="item.up ? 'kpi-change--up' : 'kpi-change--down'">
              {{ item.up ? '↑' : '↓' }} {{ formatPct(item.changePct) }}%
            </span>
            <span class="kpi-prev">{{ item.prevLabel || '上期' }} {{ formatInt(item.prevValue) }}</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card__label">注册终端分布</div>
          <div class="kpi-terminal">
            <span v-for="t in terminals" :key="t.name" class="kpi-terminal__item">
              {{ t.name }} <b>{{ t.pct }}%</b>
            </span>
            <span v-if="!terminals.length" class="kpi-terminal__empty">-</span>
          </div>
        </div>
      </div>

      <!-- 活跃度 + 漏斗 -->
      <div class="ur-charts">
        <div class="ur-card">
          <div class="ur-card__header">
            <span class="ur-card__head">
              <span class="ur-card__title">用户活跃度分布</span>
              <span class="activity-help">
                ?
                <div class="activity-tip">
                  <div class="activity-tip__head">
                    活跃度定义 <em>（T=成交天数，L=登录天数）</em>
                  </div>
                  <div class="activity-tip__row">
                    <div class="activity-tip__lv"><b class="is-high">高活跃</b> <span>T ≥ 3</span></div>
                    <div class="activity-tip__desc">窗口内至少3天有真实成交，是平台交易量的核心贡献者</div>
                  </div>
                  <div class="activity-tip__row">
                    <div class="activity-tip__lv"><b class="is-mid">中活跃</b> <span>1 ≤ T ≤ 2</span></div>
                    <div class="activity-tip__desc">窗口内有成交但频次不高，属于低频交易客户</div>
                  </div>
                  <div class="activity-tip__row">
                    <div class="activity-tip__lv"><b class="is-low">低活跃</b> <span>T = 0 且 L ≥ 1</span></div>
                    <div class="activity-tip__desc">窗口内没有任何成交，但有登录/浏览行为，是有留存但未转化交易的用户</div>
                  </div>
                  <div class="activity-tip__row">
                    <div class="activity-tip__lv"><b class="is-sleep">沉睡</b> <span>T = 0 且 L = 0</span></div>
                    <div class="activity-tip__desc">窗口内既无登录也无成交，完全无平台触达行为</div>
                  </div>
                </div>
              </span>
            </span>
            <span class="ur-card__note">观察窗口：近7天</span>
          </div>
          <div class="ur-card__body">
            <div ref="activityChart" class="chart"></div>
          </div>
        </div>
        <div class="ur-card">
          <div class="ur-card__header">
            <span class="ur-card__title">用户转化漏斗</span>
          </div>
          <div class="ur-card__body">
            <div ref="funnelChart" class="chart"></div>
          </div>
        </div>
      </div>

      <!-- 标签云 + 资产 -->
      <div class="ur-charts">
        <div class="ur-card">
          <div class="ur-card__header">
            <span class="ur-card__title">用户标签分布</span>
            <span class="ur-card__stats">
              <span>标签类型 <b>{{ tagStats.tagTypes }}</b></span>
              <span>打标用户 <b>{{ tagStats.taggedUsers }}</b></span>
              <span>高净值 <b>{{ tagStats.hnwi }}</b></span>
            </span>
          </div>
          <div class="ur-card__body">
            <div class="tag-cloud">
              <span
                v-for="tag in tagStats.items"
                :key="tag.name"
                class="tag-chip"
                :style="tagChipStyle(tag)"
                :title="tag.count + ' 人'"
              >
                {{ tag.name }}<span class="tag-chip__count">({{ tag.count }})</span>
              </span>
              <span v-if="!tagStats.items || !tagStats.items.length" class="empty-tip">暂无标签数据</span>
            </div>
          </div>
        </div>
        <div class="ur-card">
          <div class="ur-card__header">
            <span class="ur-card__title">资产等级分布</span>
          </div>
          <div class="ur-card__body">
            <div ref="assetChart" class="chart chart--asset"></div>
            <div class="asset-foot">
              <span>总客户数 <b>{{ formatInt(asset.total) }}</b></span>
              <span>高净值客户 <b>{{ formatInt(asset.hnwi) }}</b></span>
              <span>高净值占比 <b>{{ asset.hnwiPct }}%</b></span>
            </div>
            <div class="asset-caption">依据：账户净值快照（USD）</div>
          </div>
        </div>
      </div>

      <!-- 转化时效 -->
      <div class="ur-card">
        <div class="ur-card__header">
          <span class="ur-card__title">转化时效分析</span>
          <span class="ur-card__note">单位：小时</span>
        </div>
        <div class="ur-card__body">
          <table class="timing-table">
            <thead>
              <tr>
                <th>环节</th>
                <th>平均耗时</th>
                <th>最短</th>
                <th>最长</th>
                <th>样本数</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in timing" :key="row.name">
                <td>{{ row.name }}</td>
                <td>{{ formatHours(row.avg) }}</td>
                <td>{{ formatHours(row.min) }}</td>
                <td>{{ formatHours(row.max) }}</td>
                <td>{{ formatInt(row.count) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="timing-total">
            完整转化周期平均：<b>{{ formatHours(fullCycle.avg) }}</b>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="ur-card">
      <div class="ur-card__body">
        <el-form :inline="true" :model="search" size="small" class="ur-filter">
          <el-form-item>
            <el-input
              v-model="search.keyword"
              placeholder="用户ID/姓名/邮箱"
              clearable
              class="filter-input"
              @keyup.enter.native="onSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-select v-model="search.activeStatus" placeholder="激活状态" clearable class="filter-select">
              <el-option label="已激活" :value="2" />
              <el-option label="未激活" :value="1" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select v-model="search.regTerminal" placeholder="注册终端" clearable class="filter-select">
              <el-option label="iOS" :value="3" />
              <el-option label="Android" :value="2" />
              <el-option label="H5" :value="1" />
              <el-option label="PC" :value="4" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-date-picker
              v-model="activateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="激活开始"
              end-placeholder="激活结束"
              class="filter-range"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" size="small" @click="onSearch">🔍 搜索</el-button>
            <el-button size="small" @click="onReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 用户列表 -->
    <div class="ur-card">
      <div class="ur-card__header">
        <span class="ur-card__title">用户列表</span>
        <button class="export-btn" @click="handleExport">📥 导出</button>
      </div>
      <div class="ur-card__body">
        <el-table v-loading="listLoading" :data="tableData" border style="width:100%;">
          <el-table-column prop="customerNo" label="用户ID" width="90" />
          <el-table-column prop="name" label="姓名" min-width="110" show-overflow-tooltip />
          <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
          <el-table-column label="注册终端" width="100" align="center">
            <template slot-scope="{ row }">
              <span class="terminal-chip">{{ row.regWayName || '未知' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="激活状态" width="100" align="center">
            <template slot-scope="{ row }">
              <span v-if="row.activeStatus === 2" class="status-active">已激活</span>
              <span v-else class="status-inactive">未激活</span>
            </template>
          </el-table-column>
          <el-table-column label="余额（USD）" width="140" align="right">
            <template slot-scope="{ row }">
              <span class="mono">{{ formatMoney(row.balance) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="标签" min-width="170">
            <template slot-scope="{ row }">
              <span v-for="tag in row.tags" :key="tag" class="tag-mini">{{ tag }}</span>
              <span v-if="!row.tags || !row.tags.length" class="empty-tip">-</span>
            </template>
          </el-table-column>
          <el-table-column label="入金权限" width="100" align="center">
            <template slot-scope="{ row }">
              <span :class="row.deposit === 1 ? 'perm-allow' : 'perm-deny'">{{ permissionText(row.deposit) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="出金权限" width="100" align="center">
            <template slot-scope="{ row }">
              <span :class="row.withdraw === 1 ? 'perm-allow' : 'perm-deny'">{{ permissionText(row.withdraw) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="regTime" label="注册时间" width="160" />
          <el-table-column prop="activateTime" label="激活时间" width="160" />
        </el-table>
        <div class="ur-pagination">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :total="total"
            :current-page="search.pageNum"
            :page-size="search.pageSize"
            :page-sizes="[15, 30, 50]"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { PieChart, FunnelChart, BarChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { userReportOverview, userReportList, userReportExport } from '@/api/userReport'

echarts.use([PieChart, FunnelChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const COLOR_MUTED = '#9ca3af'
const COLOR_BORDER = '#e8ecf2'
const ACTIVITY_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#9ca3af']
const FUNNEL_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444']

const RANGE_OPTIONS = [
  { key: 'today', label: '今日' },
  { key: '7d', label: '近7天' },
  { key: '1m', label: '近1月' },
  { key: '3m', label: '近3月' },
  { key: '6m', label: '近半年' },
  { key: '1y', label: '近1年' }
]

/** Date → yyyy-MM-dd（本地时区，避免 toISOString 的 UTC 偏移） */
function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'UserReportList',
  data() {
    return {
      rangeOptions: RANGE_OPTIONS,
      range: 'today',
      customFrom: '',
      customTo: '',
      overviewLoading: false,
      listLoading: false,
      overview: null,
      search: {
        pageNum: 1,
        pageSize: 15,
        keyword: '',
        activeStatus: null,
        regTerminal: null
      },
      activateRange: [],
      tableData: [],
      total: 0,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        }
      }
    }
  },
  computed: {
    kpi() {
      return (this.overview && this.overview.kpi) || []
    },
    terminals() {
      return (this.overview && this.overview.terminals) || []
    },
    tagStats() {
      return (this.overview && this.overview.tags) || { tagTypes: 0, taggedUsers: 0, hnwi: 0, items: [] }
    },
    asset() {
      return (this.overview && this.overview.asset) || { total: 0, hnwi: 0, hnwiPct: 0, tiers: [] }
    },
    timing() {
      return (this.overview && this.overview.timing) || []
    },
    fullCycle() {
      return (this.overview && this.overview.fullCycle) || { name: '完整转化周期', avg: null }
    }
  },
  created() {
    // 图表实例与定时器不参与响应式，避免 Vue 深度代理 echarts 对象
    this.charts = {}
    this.resizeTimer = null
    this.applyRange('today')
  },
  mounted() {
    this.initCharts()
    window.addEventListener('resize', this.handleResize)
    this.loadOverview()
    this.fetchList()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    Object.keys(this.charts || {}).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].dispose()
        this.charts[key] = null
      }
    })
  },
  methods: {
    // ---------- 时间范围 ----------
    /** 计算预置区间的起止日期（与原型一致：近N月/年按整月整年回退，不减一天） */
    resolvePreset(key) {
      const to = new Date()
      const from = new Date()
      switch (key) {
        case '7d':
          from.setDate(from.getDate() - 6)
          break
        case '1m':
          from.setMonth(from.getMonth() - 1)
          break
        case '3m':
          from.setMonth(from.getMonth() - 3)
          break
        case '6m':
          from.setMonth(from.getMonth() - 6)
          break
        case '1y':
          from.setFullYear(from.getFullYear() - 1)
          break
        default:
          break
      }
      return { from: formatDate(from), to: formatDate(to) }
    },

    /** 切换预置区间：同步日期选择器为区间端点，便于用户在此基础上微调 */
    applyRange(key) {
      this.range = key
      const resolved = this.resolvePreset(key)
      this.customFrom = resolved.from
      this.customTo = resolved.to
    },

    selectRange(key) {
      this.applyRange(key)
      this.loadOverview()
    },

    /** 自定义日期：两端都有效才触发查询 */
    onCustomRange() {
      if (!this.customFrom || !this.customTo) {
        return
      }
      if (this.customFrom > this.customTo) {
        this.$message.warning('开始日期不能晚于结束日期')
        return
      }
      this.range = 'custom'
      this.loadOverview()
    },

    /** 刷新：预置区间按当前时刻重算，自定义区间保持原样 */
    refresh() {
      if (this.range !== 'custom') {
        this.applyRange(this.range)
      }
      this.loadOverview()
      this.fetchList()
    },

    // ---------- 概览 ----------
    loadOverview() {
      this.overviewLoading = true
      userReportOverview({ from: this.customFrom, to: this.customTo })
        .then(res => {
          this.overview = res.result || null
          this.renderCharts()
        })
        .finally(() => {
          this.overviewLoading = false
        })
    },

    /** 图表实例集中管理，避免重复 init 与实例泄漏 */
    initCharts() {
      this.charts = {
        activity: echarts.init(this.$refs.activityChart),
        funnel: echarts.init(this.$refs.funnelChart),
        asset: echarts.init(this.$refs.assetChart)
      }
    },

    renderCharts() {
      const activity = (this.overview && this.overview.activity) || []
      const funnel = (this.overview && this.overview.funnel) || []
      const tiers = (this.overview && this.overview.asset && this.overview.asset.tiers) || []
      this.charts.activity.setOption(this.buildActivityOption(activity), true)
      this.charts.funnel.setOption(this.buildFunnelOption(funnel), true)
      this.charts.asset.setOption(this.buildAssetOption(tiers), true)
    },

    /** 活跃度饼：四档互斥，tooltip 展示人数与占比 */
    buildActivityOption(rows) {
      return {
        color: ACTIVITY_COLORS,
        tooltip: { trigger: 'item', formatter: '{b}: {c}人 ({d}%)' },
        legend: { bottom: 0, itemWidth: 10, itemHeight: 8, textStyle: { fontSize: 11, color: COLOR_MUTED } },
        series: [
          {
            type: 'pie',
            radius: ['40%', '65%'],
            center: ['50%', '42%'],
            avoidLabelOverlap: true,
            label: { formatter: '{b}\n{c}人', fontSize: 11, color: '#4b5563' },
            labelLine: { length: 6, length2: 6 },
            data: rows.map(row => ({ name: row.name, value: row.value }))
          }
        ]
      }
    },

    /** 转化漏斗：累计到达人数，「总转化率 / 环比转化率」由相邻档位派生 */
    buildFunnelOption(rows) {
      const counts = rows.map(row => Number(row.value) || 0)
      const base = counts.length ? counts[0] : 0
      return {
        color: FUNNEL_COLORS,
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const index = params.dataIndex
            const current = counts[index] || 0
            const total = base > 0 ? (current * 100 / base).toFixed(1) : '0.0'
            const prev = index > 0 ? counts[index - 1] : 0
            const ring = index > 0 ? (prev > 0 ? (current * 100 / prev).toFixed(1) : '0.0') : '100.0'
            return `${params.name}: ${current}人<br/>总转化率: ${total}%<br/>环比转化率: ${ring}%`
          }
        },
        series: [
          {
            type: 'funnel',
            left: '10%',
            right: '10%',
            top: 10,
            bottom: 10,
            minSize: '20%',
            maxSize: '100%',
            sort: 'descending',
            gap: 2,
            label: { position: 'inside', formatter: '{b}\n{c}人', color: '#fff', fontWeight: 'bold', fontSize: 11 },
            itemStyle: { borderWidth: 0 },
            data: rows.map(row => ({ name: row.name, value: row.value }))
          }
        ]
      }
    },

    /** 资产等级柱：6 档由低到高，柱顶显示人数 */
    buildAssetOption(rows) {
      return {
        grid: { top: 18, right: 20, bottom: 25, left: 50 },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: '{b}: {c}人' },
        xAxis: {
          type: 'category',
          data: rows.map(row => row.name),
          axisLabel: { fontSize: 10, color: COLOR_MUTED, interval: 0, hideOverlap: true },
          axisLine: { lineStyle: { color: COLOR_BORDER } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#f1f5f9' } },
          axisLabel: { fontSize: 10, color: COLOR_MUTED },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 32,
            label: { show: true, position: 'top', fontSize: 10, color: '#6b7280' },
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#8b5cf6' },
                  { offset: 1, color: 'rgba(139, 92, 246, 0.35)' }
                ]
              }
            },
            data: rows.map(row => row.value)
          }
        ]
      }
    },

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        Object.keys(this.charts || {}).forEach(key => {
          if (this.charts[key]) {
            this.charts[key].resize()
          }
        })
      }, 200)
    },

    // ---------- 用户列表 ----------
    buildListParams() {
      const params = {
        pageNum: this.search.pageNum,
        pageSize: this.search.pageSize,
        keyword: this.search.keyword || undefined,
        activeStatus: this.search.activeStatus,
        regTerminal: this.search.regTerminal
      }
      if (this.activateRange && this.activateRange.length === 2) {
        params.activateFrom = this.activateRange[0]
        params.activateTo = this.activateRange[1]
      }
      return params
    },

    fetchList() {
      this.listLoading = true
      userReportList(this.buildListParams())
        .then(res => {
          const result = res.result || {}
          this.tableData = result.list || []
          this.total = result.total || 0
        })
        .finally(() => {
          this.listLoading = false
        })
    },

    onSearch() {
      this.search.pageNum = 1
      this.fetchList()
    },

    onReset() {
      this.search = {
        pageNum: 1,
        pageSize: this.search.pageSize,
        keyword: '',
        activeStatus: null,
        regTerminal: null
      }
      this.activateRange = []
      this.fetchList()
    },

    handlePageChange(page) {
      this.search.pageNum = page
      this.fetchList()
    },

    handleSizeChange(size) {
      this.search.pageSize = size
      this.search.pageNum = 1
      this.fetchList()
    },

    /** 导出：不带分页，后端按筛选条件 keyset 游标全量扫描 */
    handleExport() {
      const params = this.buildListParams()
      delete params.pageNum
      delete params.pageSize
      userReportExport(params).then(res => {
        const blob = res instanceof Blob
          ? res
          : new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '用户数据报表.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
      })
    },

    // ---------- 展示格式化 ----------
    formatInt(value) {
      if (value === null || value === undefined) {
        return '0'
      }
      return Number(value).toLocaleString('en-US')
    },

    formatPct(value) {
      return Number(value).toFixed(1)
    },

    formatMoney(value) {
      const num = Number(value || 0)
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatHours(value) {
      if (value === null || value === undefined) {
        return '-'
      }
      return `${Number(value).toFixed(1)} 小时`
    },

    /** 权限值：1 允许 / 2 禁止 / 空 未设置 */
    permissionText(value) {
      if (value === 1) {
        return '允许'
      }
      if (value === 2) {
        return '禁止'
      }
      return '-'
    },

    /** 标签云：字号随人数线性放大，颜色取标签自身配置色 */
    tagChipStyle(tag) {
      const items = this.tagStats.items || []
      const max = items.reduce((acc, cur) => Math.max(acc, Number(cur.count) || 0), 0)
      const count = Number(tag.count) || 0
      const fontSize = max > 0 ? 12 + Math.round((count / max) * 10) : 12
      return { fontSize: `${fontSize}px`, color: tag.color || '#4b5563' }
    }
  }
}
</script>

<style lang="scss" scoped>
.user-report {
  padding: 20px;
  background: #f7f9fc;
}

/* ---------- 头部 ---------- */
.ur-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  &__title {
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: #111827;
    }

    p {
      margin: 4px 0 0;
      font-size: 12px;
      color: #9ca3af;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.range-btn {
  padding: 5px 12px;
  font-size: 12px;
  color: #4b5563;
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    color: #3b82f6;
    border-color: #bfdbfe;
  }

  &--active {
    color: #fff;
    background: #3b82f6;
    border-color: #3b82f6;
  }
}

.range-sep {
  font-size: 12px;
  color: #9ca3af;
}

.range-date {
  width: 140px;
}

/* ---------- 卡片 ---------- */
.ur-overview {
  min-height: 200px;
}

.ur-card {
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 4px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e8ecf2;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }

  &__note {
    font-size: 11px;
    color: #9ca3af;
  }

  &__stats {
    display: flex;
    gap: 14px;
    font-size: 11px;
    color: #9ca3af;

    b {
      color: #111827;
      font-size: 13px;
    }
  }

  &__body {
    padding: 16px;
  }
}

/* ---------- 活跃度口径帮助气泡（纯 CSS hover，无需 JS 状态） ---------- */
.activity-help {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #f3f4f6;
  border: 1px solid #e8ecf2;
  border-radius: 50%;
  color: #9ca3af;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  cursor: help;

  &:hover .activity-tip {
    display: block;
  }
}

.activity-tip {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 2000;
  width: 300px;
  padding: 12px 14px;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  font-weight: normal;
  line-height: 1.6;
  text-align: left;
  cursor: default;

  &__head {
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e8ecf2;
    color: #111827;
    font-weight: 600;

    em {
      color: #9ca3af;
      font-size: 11px;
      font-style: normal;
      font-weight: normal;
    }
  }

  &__row {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__lv {
    color: #9ca3af;

    b.is-high {
      color: #237804;
    }

    b.is-mid {
      color: #1890ff;
    }

    b.is-low {
      color: #fa8c16;
    }

    b.is-sleep {
      color: #8c8c8c;
    }
  }

  &__desc {
    color: #4b5563;
  }
}

/* ---------- KPI ---------- */
.ur-kpi {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.kpi-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 4px;

  &__label {
    font-size: 12px;
    color: #9ca3af;
  }

  &__value {
    margin-top: 6px;
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
  }
}

.kpi-change {
  font-weight: 600;

  &--up {
    color: #10b981;
  }

  &--down {
    color: #ef4444;
  }
}

.kpi-prev {
  color: #9ca3af;
}

.kpi-terminal {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  font-size: 12px;
  color: #4b5563;

  b {
    color: #111827;
  }

  &__empty {
    color: #9ca3af;
  }
}

/* ---------- 图表区 ---------- */
.ur-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  .ur-card {
    margin-bottom: 0;
  }
}

.chart {
  height: 260px;

  &--asset {
    height: 220px;
  }
}

/* ---------- 标签云 ---------- */
.tag-cloud {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-height: 220px;
  align-content: center;
}

.tag-chip {
  padding: 4px 12px;
  border-radius: 16px;
  background: #f3f4f6;
  font-weight: 600;
  cursor: default;
  transition: transform 0.15s;

  &:hover {
    transform: scale(1.1);
  }

  &__count {
    margin-left: 4px;
    font-weight: 400;
    font-size: 0.85em;
    opacity: 0.7;
  }
}

.empty-tip {
  font-size: 12px;
  color: #9ca3af;
}

/* ---------- 资产脚注 ---------- */
.asset-foot {
  display: flex;
  gap: 20px;
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;

  b {
    color: #111827;
  }
}

.asset-caption {
  margin-top: 4px;
  font-size: 11px;
  color: #9ca3af;
}

/* ---------- 时效表 ---------- */
.timing-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    background: #f9fafb;
  }

  td:first-child {
    color: #374151;
    font-weight: 500;
  }
}

.timing-total {
  margin-top: 12px;
  font-size: 13px;
  color: #4b5563;

  b {
    color: #111827;
  }
}

/* ---------- 筛选栏 ---------- */
.ur-filter {
  margin-bottom: -18px;

  .filter-input {
    width: 200px;
  }

  .filter-select {
    width: 120px;
  }

  .filter-range {
    width: 250px;
  }
}

/* ---------- 列表 ---------- */
.export-btn {
  padding: 4px 12px;
  font-size: 12px;
  color: #4b5563;
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: #3b82f6;
    border-color: #bfdbfe;
  }
}

.terminal-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 4px;
}

.status-active {
  font-weight: 600;
  color: #10b981;
}

.status-inactive {
  color: #9ca3af;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.tag-mini {
  display: inline-block;
  margin: 1px 4px 1px 0;
  padding: 1px 8px;
  font-size: 10px;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 10px;
}

.perm-allow {
  font-weight: 600;
  color: #10b981;
}

.perm-deny {
  font-weight: 600;
  color: #ef4444;
}

.ur-pagination {
  margin-top: 14px;
  text-align: right;
}

@media screen and (max-width: 1400px) {
  .ur-kpi {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
